import React, { useState } from "react";
import { Watch, ShieldAlert, Award, TrendingUp, AlertTriangle, FileText, Download, Eye, Cog, Hash, Link as LinkIcon, X, ZoomIn } from "lucide-react";

const IMG_WATCH = "https://images.unsplash.com/photo-1768062251809-739d987a42fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtZWNoYW5pY2FsJTIwd2F0Y2glMjBtYWNyb3xlbnwwfHx8fDE3NzY2NzYwMDR8MA&ixlib=rb-4.1.0&q=85";
const IMG_MOVEMENT = "https://customer-assets.emergentagent.com/job_precious-exchange-6/artifacts/wzlqmx3r_Real%20vs%20Fake.jpg.webp";
const IMG_SERIAL = "https://customer-assets.emergentagent.com/job_precious-exchange-6/artifacts/z945w9w5_Rolex%20serial%20number.jpg.jpeg";
const IMG_BRACELET = "https://customer-assets.emergentagent.com/job_precious-exchange-6/artifacts/35v85cut_Rolex%20bracelet.webp";

export default function WatchesPage({ onDownload, documents }) {
  const ncndaDoc = documents?.find((d) => d.id === "doc-ncnda");
  const [zoomedImg, setZoomedImg] = useState(null);

  return (
    <div data-testid="view-watches" className="max-w-5xl">
      <Hero />

      <Section eyebrow="Marché" title="L'économie réelle de l'horlogerie de luxe" index="01">
        <p>
          Le marché secondaire des montres de luxe est un <strong className="text-white">écosystème parallèle</strong>
          où les prix n'obéissent pas aux tarifs officiels. Sur les pièces les plus recherchées, la prime dépasse
          couramment 200 %, parfois 1 000 %. Comprendre la production, la rareté et les risques est
          <strong className="text-white"> la seule manière de ne pas se brûler les doigts</strong>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
          <KPI value="~80 Md €" label="Marché primaire mondial" sub="Ventes boutique neuves" />
          <KPI value="~28 Md €" label="Marché secondaire" sub="Revente pré-owned certifié" />
          <KPI value="+40 %" label="Croissance 5 ans" sub="Pré-owned > boutique depuis 2020" accent />
        </div>
      </Section>

      <Section eyebrow="Les 5 piliers" title="Production, histoire et cote des 5 marques d'exception" index="02">
        <p>
          Ces cinq maisons concentrent 90 % de la demande pré-owned premium. Chacune a son ADN, sa production et
          son écart boutique / marché propre.
        </p>

        {[
          {
            name: "Rolex",
            tag: "Genève · depuis 1905",
            capacity: "~1 200 000 montres / an",
            history: "Fondée par Hans Wilsdorf. Première montre certifiée chronomètre en 1910. Oyster (1926) = première étanche. Submariner (1953), GMT-Master (1955), Daytona (1963) — trois icônes qui structurent le marché secondaire actuel.",
            boutique: "8 000–45 000 €",
            market: "12 000–250 000 €",
            spread: "+40 % à +600 %",
            hot: "Daytona 116500LN, GMT-Master II 126710BLRO 'Pepsi', Submariner Hulk",
            note: "Demande structurellement supérieure à la production. Liste d'attente chez les AD : 2 à 8 ans sur les modèles sport. C'est ce gap qui crée la prime du marché gris.",
          },
          {
            name: "Audemars Piguet",
            tag: "Le Brassus · depuis 1875",
            capacity: "~50 000 montres / an",
            history: "Fondée par Jules-Louis Audemars et Edward-Auguste Piguet. La Royal Oak (1972), dessinée par Gérald Genta, inaugure la montre de sport en acier à prix d'horlogerie haute — rupture absolue. Aujourd'hui 95 % des ventes = Royal Oak et Royal Oak Offshore.",
            boutique: "25 000–250 000 €",
            market: "40 000–800 000 €",
            spread: "+60 % à +400 %",
            hot: "Royal Oak 15202, Royal Oak Offshore 26470, Royal Oak Concept",
            note: "Production volontairement limitée. Distribution ultra-sélective. La Royal Oak Jumbo 15202 a disparu du catalogue fin 2022 → sa cote a bondi de +80 % en 18 mois.",
          },
          {
            name: "Patek Philippe",
            tag: "Genève · depuis 1839",
            capacity: "~72 000 montres / an",
            history: "Maison de référence absolue en complications. Première montre-bracelet (1868), grande sonnerie (1910), quantième perpétuel chrono (1941, réf. 1518). Nautilus (1976, Gérald Genta) = équivalent Royal Oak. 'You never actually own a Patek Philippe, you merely look after it for the next generation' — slogan 1996 qui résume l'ADN.",
            boutique: "30 000–500 000 €",
            market: "50 000–3 000 000 €",
            spread: "+80 % à +800 %",
            hot: "Nautilus 5711/1A (discontinuée), Aquanaut 5167, Grand Complications",
            note: "Processus d'achat le plus difficile du secteur. Les sightholders sont sélectionnés sur historique d'achat, pedigree et relation personnelle avec la manufacture.",
          },
          {
            name: "Richard Mille",
            tag: "Les Breuleux · depuis 2001",
            capacity: "~5 500 montres / an",
            history: "Créée par Richard Mille. Philosophie : appliquer les matériaux de la F1 et de l'aéronautique à l'horlogerie (carbone TPT, quartz TPT, titane grade 5, saphir). RM 001 (2001) à 135 000 € — tollé initial, succès phénoménal depuis. Ambassadeurs : Nadal, Alonso, Bolt.",
            boutique: "90 000–1 200 000 €",
            market: "150 000–3 500 000 €",
            spread: "+60 % à +300 %",
            hot: "RM 011 Felipe Massa, RM 27 Nadal, RM 055 Bubba Watson",
            note: "Production ultra-limitée. Matériaux quasi-impossibles à contrefaire correctement (le carbone TPT nécessite un équipement industriel rare). Clientèle ultra-exclusive.",
          },
          {
            name: "F.P. Journe",
            tag: "Genève · depuis 1999",
            capacity: "~900 montres / an",
            history: "François-Paul Journe — chronométrier virtuose. Fonde sa maison à 42 ans après des années comme restaurateur de pièces anciennes. Chronomètre Souverain (2000), Tourbillon Souverain (1999, premier tourbillon avec remontoir constant), Élégante (2014 — seule pièce quartz, conçue avec un système de veille unique).",
            boutique: "25 000–150 000 €",
            market: "50 000–1 200 000 €",
            spread: "+100 % à +900 %",
            hot: "Chronomètre Bleu, Tourbillon Souverain, Élégante (dame)",
            note: "La pépite du collectionneur absolu. Exemple type : une Élégante acier 40 mm — prix boutique 20 000 €, prix marché actuel ~150 000 €. Raison profonde : aucune contrefaçon crédible n'existe, car la chaîne d'approvisionnement (or rose 18K calibre, mouvements maison, boîtiers spéciaux) est totalement interne et non reproduite. Les collectionneurs achètent avec une sécurité rare.",
            premium: true,
          },
        ].map((b) => (
          <BrandCard key={b.name} {...b} />
        ))}

        <Callout icon={TrendingUp} title="Lecture intermédiaire">
          La marge de l'intermédiaire n'est <em>jamais</em> le spread boutique-marché complet : entre l'Authorized Dealer
          et le client final passent AD → distributeur gris → broker wholesale → broker retail → client. Le broker
          raisonnable capture <strong className="text-white">3 à 8 %</strong> sur un deal. Au-delà, c'est soit une pièce
          rare avec commission ponctuelle, soit une marge hollywood qui ne dure pas.
        </Callout>
      </Section>

      <Section eyebrow="Risques" title="Les 4 grandes arnaques en horlogerie secondaire" index="03">
        <p>
          Une montre de luxe est un assemblage de <strong className="text-white">80 à 350 composants</strong>.
          Chacun peut être remplacé, remis à neuf, contrefait. Même si la pièce maîtresse est authentique, le reste
          peut être bidon. <strong className="text-white">Une seule vente foireuse impacte votre réputation pour des
          années</strong> — d'où l'obligation de jouer safe, quitte à faire moins de marge.
        </p>

        <div className="mt-10 space-y-4">
          <RiskCard
            level="CRITIQUE"
            title="Pièces after-market (franken-watches)"
            icon={AlertTriangle}
            body="Le cadran, l'aiguille, la couronne, le bracelet, voire le mouvement peuvent être remplacés par des pièces génériques de qualité supérieure — mais non-Rolex, non-AP, non-Patek. La montre tourne, semble vraie, mais perd 50–80 % de sa valeur. Nommée 'franken-watch' dans le jargon."
            signs={[
              "Cadran légèrement décoloré ou trop 'frais' pour l'âge",
              "Laquage de couronne ou logo imparfait (micro-différences)",
              "Numéro de série gravé non-aligné avec la référence / année",
              "Poids légèrement inférieur (bracelet after-market en acier standard)",
            ]}
          />
          <RiskCard
            level="ÉLEVÉ"
            title="Faux certificats et fausses boîtes"
            icon={ShieldAlert}
            body="Une montre 100 % authentique vendue avec un Warranty Card trafiqué (date, nom dealer, numéro de série différent) vaut 30–40 % de moins. Idem pour les boîtiers d'origine : une Rolex 'full set' vaut +15–20 % vs 'head only'. Les contrefacteurs savent tout reproduire — y compris les hologrammes récents."
            signs={[
              "Encre du warranty card légèrement différente entre champs",
              "Poids de la boîte en contreplaqué vs boîtier d'origine bois massif",
              "Hologramme ou QR code absent ou flou",
              "Numéros série boîtier ≠ numéro série gravé dans la carrure",
            ]}
          />
          <RiskCard
            level="ÉLEVÉ"
            title="Services aftermarket non-déclarés"
            icon={AlertTriangle}
            body="Un mouvement servicé hors manufacture (pièces 'génériques' compatibles) affecte la cote. Même si la montre fonctionne parfaitement, un collectionneur informé refusera ou négociera −15 à −25 %."
            signs={[
              "Absence de service history manufacture",
              "Vis du mouvement montrant des traces de dévissage non-factory",
              "Numéros de gravure mouvement différents des archives maison",
            ]}
          />
          <RiskCard
            level="MOYEN"
            title="Réf. polies/repolished à mort"
            icon={Eye}
            body="Une montre repolie agressivement perd ses arêtes d'origine — invisible à l'œil non entraîné mais détectable à la loupe x10. Impact prix : −10 à −20 %. Certaines réfs vintage voient leur prime s'effondrer si le polissage dépasse 2 services."
            signs={[
              "Arêtes des cornes trop arrondies vs photos catalogue d'époque",
              "Épaisseur du boîtier inférieure aux specs usine (~0,1–0,2 mm)",
              "Biseaux de cornes quasi-effacés",
            ]}
          />
        </div>
      </Section>

      <Section eyebrow="Vérifications d'expert" title="Démasquer une fausse Rolex : 3 contrôles qui tuent" index="04">
        <p>
          Les contrefaçons haut-de-gamme sont devenues redoutables : poids correct, étanchéité, verre saphir.
          L'œil non-entraîné s'y trompe. Mais il existe <strong className="text-white">trois zones d'observation</strong>
          qui révèlent systématiquement la supercherie — ou l'authentique.
        </p>

        {/* Movement check */}
        <div className="mt-12 border border-white/10 bg-[#0c0c0c]">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="aspect-[4/3] lg:aspect-auto bg-[#141414] overflow-hidden relative group cursor-zoom-in"
                 onClick={() => setZoomedImg({ src: IMG_MOVEMENT, alt: "Real vs Fake Rolex movement" })}
                 data-testid="open-movement-zoom">
              <img src={IMG_MOVEMENT} alt="Real vs Fake Rolex movement" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 border border-[#D4AF37]/60 px-4 py-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  <ZoomIn className="h-3.5 w-3.5" /> Agrandir la photo
                </div>
              </div>
            </div>
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3">
                <Cog className="h-5 w-5 text-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Contrôle 01 · Le mouvement</span>
              </div>
              <h3 className="font-serif text-2xl mt-4 text-white">Ouvrir la montre, regarder le calibre</h3>
              <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">
                C'est <strong className="text-white">le test le plus fiable</strong>. Une vraie Rolex abrite un calibre
                maison (3135, 3235, 4130, 3285…) avec une finition impeccable : côtes de Genève sur le rotor, gravure
                <em> Perlage</em> sur les ponts, vis polies bleuies ou satinées selon les ponts, signature Rolex gravée
                nette, numéro de série gravé sur la carrure (pas sur le mouvement lui-même).
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 text-xs">
                <div className="border border-[#34C759]/30 bg-[#34C759]/5 p-4">
                  <div className="uppercase tracking-[0.22em] text-[#34C759] text-[10px]">Authentique</div>
                  <ul className="mt-3 space-y-1.5 text-[#E4E4E7]">
                    <li>· Finition <em>perlage</em> parfaite</li>
                    <li>· Rotor gravé Rolex propre</li>
                    <li>· Vis polies, têtes non rayées</li>
                    <li>· Balancier Microstella visible</li>
                  </ul>
                </div>
                <div className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-4">
                  <div className="uppercase tracking-[0.22em] text-[#FF3B30] text-[10px]">Contrefaçon</div>
                  <ul className="mt-3 space-y-1.5 text-[#E4E4E7]">
                    <li>· Mouvement ETA/Miyota rebadgé</li>
                    <li>· Finition décorative approximative</li>
                    <li>· Vis standard, traces d'outils</li>
                    <li>· Gravure Rolex irrégulière</li>
                  </ul>
                </div>
              </div>
              <p className="mt-5 text-xs text-[#52525B] italic">
                Conseil : ouvrir le fond nécessite un outil spécial Rolex (clé à ergots) — emmener la montre chez un
                horloger indépendant pour 30 à 60 € plutôt que perdre 10 000 € sur un faux convaincant.
              </p>
            </div>
          </div>
        </div>

        {/* Serial number metal trick */}
        <div className="mt-8 border border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c]">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-8 md:p-10 lg:order-2">
              <div className="flex items-center gap-3">
                <Hash className="h-5 w-5 text-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Astuce secrète · 02</span>
              </div>
              <h3 className="font-serif text-2xl mt-4 text-white">Le dernier caractère du numéro de série trahit le métal</h3>
              <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">
                Peu de brokers le savent : sur la plupart des Rolex, le <strong className="text-white">dernier caractère
                du numéro de série</strong> (gravé à 6h, entre les cornes) encode le type de métal utilisé pour la boîte.
                Une lecture rapide de ce caractère vous dit immédiatement si la montre est en acier, or, platine ou
                deux-tons — sans ouvrir la montre, sans expertise. Le numéro complet se vérifie aussi sur l'image
                ci-contre : gravure nette, arêtes vives, profondeur régulière = authentique.
              </p>
              <div className="mt-6 space-y-2 text-sm font-mono">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Dernier caractère → métal</div>
                {[
                  { tag: "V", metal: "Acier inoxydable 904L", price: "Base" },
                  { tag: "Y / Z", metal: "Two-tone (acier + or)", price: "+40 %" },
                  { tag: "M / K", metal: "Or 18K jaune", price: "+180 %" },
                  { tag: "P / W", metal: "Or 18K blanc", price: "+200 %" },
                  { tag: "R / N", metal: "Or Everose 18K", price: "+220 %" },
                  { tag: "G / O", metal: "Platine 950", price: "+500 %" },
                ].map((r) => (
                  <div key={r.tag} className="grid grid-cols-12 gap-4 border-b border-white/10 pb-2">
                    <div className="col-span-2 text-[#D4AF37]">{r.tag}</div>
                    <div className="col-span-7 text-white">{r.metal}</div>
                    <div className="col-span-3 text-right text-[#D4AF37]">{r.price}</div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs text-[#52525B] italic">
                Règle pratique : prenez le numéro complet, regardez le dernier caractère alphanumérique, comparez à
                cette table. En 5 secondes, vous savez si la montre correspond à sa référence affichée. Un numéro
                finissant par "M" sur une Sub "acier" = montre truquée (ou carrure remplacée).
              </p>
            </div>
            <div className="aspect-[4/3] lg:aspect-auto bg-[#141414] overflow-hidden lg:order-1 relative group cursor-zoom-in"
                 onClick={() => setZoomedImg({ src: IMG_SERIAL, alt: "Rolex serial number engraving" })}
                 data-testid="open-serial-zoom">
              <img src={IMG_SERIAL} alt="Rolex serial number engraving" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 border border-[#D4AF37]/60 px-4 py-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  <ZoomIn className="h-3.5 w-3.5" /> Agrandir la photo
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bracelet wear */}
        <div className="mt-8 border border-white/10 bg-[#0c0c0c]">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="aspect-[4/3] lg:aspect-auto bg-[#141414] overflow-hidden">
              <img src={IMG_BRACELET} alt="Rolex bracelet wear check" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3">
                <LinkIcon className="h-5 w-5 text-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Contrôle 03 · Le bracelet</span>
              </div>
              <h3 className="font-serif text-2xl mt-4 text-white">Mesurer le "stretch" du bracelet</h3>
              <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">
                Un bracelet Rolex neuf est <strong className="text-white">rigide</strong>. Avec le temps, les pins entre
                maillons s'usent et le bracelet s'allonge — c'est le <em>stretch</em>. Tenu par l'extrémité, un bracelet
                en bon état reste droit ; un bracelet fatigué tombe comme un ruban. Impact sur la valeur :
                <strong className="text-white"> −5 à −25 %</strong> selon l'ampleur, car le remplacement d'un bracelet
                Oyster/Jubilee/President en atelier Rolex coûte 1 500 à 12 000 €.
              </p>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex items-start gap-3 p-3 border border-[#34C759]/30 bg-[#34C759]/5">
                  <div className="text-[#34C759] font-mono text-xs mt-0.5">OK</div>
                  <div className="text-[#E4E4E7]">Bracelet rigide à l'horizontale, glisse léger entre maillons.</div>
                </div>
                <div className="flex items-start gap-3 p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
                  <div className="text-[#D4AF37] font-mono text-xs mt-0.5">−8 %</div>
                  <div className="text-[#E4E4E7]">Léger fléchissement (2–5 mm). Bracelet vivable, négociable.</div>
                </div>
                <div className="flex items-start gap-3 p-3 border border-[#FF3B30]/30 bg-[#FF3B30]/5">
                  <div className="text-[#FF3B30] font-mono text-xs mt-0.5">−25 %</div>
                  <div className="text-[#E4E4E7]">Bracelet "souple" comme un ruban, tombe à la verticale. Forte usure — remplacement nécessaire.</div>
                </div>
              </div>
              <p className="mt-5 text-xs text-[#52525B] italic">
                Astuce : tenez le bracelet fermé horizontalement par une extrémité. Un bracelet sain doit garder un
                angle &lt; 10°. S'il pend à plus de 30°, vous avez un stretch critique.
              </p>
            </div>
          </div>
        </div>

        <Callout icon={ShieldAlert} title="La règle des 3 vérifications">
          Avant tout engagement, validez les <strong className="text-white">3 points ensemble</strong> : mouvement,
          numéro de série (métal et cohérence), bracelet. Une seule de ces trois checks échouée = abandon du deal.
          Les 3 prises séparément ne coûtent rien. Ensemble, elles éliminent 95 % des arnaques Rolex.
        </Callout>
      </Section>

      <Section eyebrow="Protocole" title="La check-list de l'intermédiaire honnête" index="05">
        <p>
          Avant d'engager une pièce dans votre réseau, passez cette grille. Vos 10 minutes évitent 10 ans
          de mauvaise réputation.
        </p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            "Photos haute résolution (recto-verso-profil-mouvement-micro)",
            "Numéro de série + référence vérifiés sur base Rolex/PP/AP/RM",
            "Warranty Card + tampon AD lisible + date cohérente",
            "Full set : boîte extérieure, boîte intérieure, papiers, tags, manuel, notice",
            "Service history : au moins un service manufacture si >10 ans",
            "Authentification par tiers (WatchCSA, Chrono Diligence, expert indépendant)",
            "Provenance documentée (facture AD ou plateforme certifiée : Chrono24 Trusted, Watchfinder, Bucherer Certified)",
            "Essai physique : mouvement, aiguilles, lume UV, poids, rendu sonore",
          ].map((c, i) => (
            <div key={i} className="flex items-start gap-3 border border-white/10 bg-[#0c0c0c] p-4">
              <div className="font-mono text-[#D4AF37] text-xs mt-1">{String(i + 1).padStart(2, "0")}</div>
              <div className="text-sm text-[#E4E4E7]">{c}</div>
            </div>
          ))}
        </div>
        <Callout icon={Award} title="La règle d'or">
          <strong className="text-white">Mieux vaut refuser un deal que vendre une pièce douteuse.</strong> Le marché
          secondaire de luxe est fermé, incestueux, <em>très</em> bavard. Une seule escroquerie — même involontaire —
          vous ferme les portes pendant des années. Préférez travailler avec des sources identifiées (Authorized Dealers,
          Chrono24 Trusted, Watchfinder, Bucherer Certified Pre-Owned, maisons de vente comme Phillips, Sotheby's,
          Christie's). Marge plus faible, mais durable.
        </Callout>
      </Section>

      <Section eyebrow="Ressources" title="Documents pour un deal horlogerie" index="06">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DocRef doc={ncndaDoc} onDownload={onDownload} accent="NCNDA — Indispensable" note="Signature avant tout partage de photos HD et numéros de série (scams par capture et photoshop)." />
        </div>
        <p className="mt-6 text-xs text-[#52525B]">
          Documents spécifiques à exiger pour chaque pièce : Warranty Card original, facture d'achat AD, service record
          manufacture, rapport d'authentification indépendant (WatchCSA, Chronext, Bucherer CPO, Chrono Diligence).
        </p>
      </Section>

      {/* Image lightbox */}
      {zoomedImg && (
        <div
          data-testid="image-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          onClick={() => setZoomedImg(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setZoomedImg(null); }}
            data-testid="close-lightbox"
            className="absolute top-5 right-5 p-3 border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={zoomedImg.src}
            alt={zoomedImg.alt}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// ---------- Subcomponents ----------
const Hero = () => (
  <section className="relative mb-16 border border-white/10 bg-[#0c0c0c] overflow-hidden">
    <img src={IMG_WATCH} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
    <div className="relative p-10 md:p-14">
      <div className="flex items-center gap-3">
        <Watch className="h-5 w-5 text-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Secteur · 05</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mt-6 max-w-3xl">
        L'<em className="italic text-[#F5F5DC]">Horlogerie</em> — cinq <span className="text-[#D4AF37]">Saints-Graals</span>.
      </h1>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] leading-relaxed">
        Rolex, Audemars Piguet, Patek Philippe, Richard Mille, F.P. Journe. Histoire, capacité de production,
        écart boutique / marché — et les 4 grandes arnaques à éviter pour protéger votre réputation.
      </p>
    </div>
  </section>
);

const Section = ({ eyebrow, title, index, children }) => (
  <section className="mb-20">
    <div className="flex items-baseline gap-6 mb-8">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{index}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{eyebrow}</div>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light mt-2 leading-tight">{title}</h2>
      </div>
    </div>
    <div className="text-[#C4C4C7] leading-relaxed space-y-4 pl-0 md:pl-10">{children}</div>
  </section>
);

const Callout = ({ icon: Icon, title, children }) => (
  <div className="mt-10 border-l-2 border-[#D4AF37] bg-[#141414] p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{title}</span>
    </div>
    <div className="mt-4 text-[#E4E4E7] leading-relaxed text-sm">{children}</div>
  </div>
);

const KPI = ({ value, label, sub, accent }) => (
  <div className={`border p-6 ${accent ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c]" : "border-white/10 bg-[#0c0c0c]"}`}>
    <div className="font-serif text-3xl text-[#D4AF37]">{value}</div>
    <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-white">{label}</div>
    <div className="mt-3 text-xs text-[#A1A1AA]">{sub}</div>
  </div>
);

const BrandCard = ({ name, tag, capacity, history, boutique, market, spread, hot, note, premium }) => (
  <div className={`mt-12 border ${premium ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#141414] to-[#0c0c0c]" : "border-white/10 bg-[#0c0c0c]"} p-7 md:p-10`}>
    <div className="flex flex-wrap items-baseline justify-between gap-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{tag}</div>
        <h3 className="font-serif text-3xl md:text-4xl mt-2">{name}</h3>
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Capacité</div>
        <div className="font-mono text-white mt-1">{capacity}</div>
      </div>
    </div>

    <p className="mt-8 text-[#A1A1AA] leading-relaxed">{history}</p>

    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="border border-white/10 p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Prix boutique</div>
        <div className="font-mono text-white mt-2">{boutique}</div>
      </div>
      <div className="border border-[#D4AF37]/40 p-5 bg-[#141414]">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Prix marché secondaire</div>
        <div className="font-mono text-[#F5F5DC] mt-2">{market}</div>
      </div>
      <div className="border border-white/10 p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Spread moyen</div>
        <div className="font-mono text-[#D4AF37] mt-2">{spread}</div>
      </div>
    </div>

    <div className="mt-6 pt-6 border-t border-white/10">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Références les plus recherchées</div>
      <div className="mt-2 font-mono text-sm text-white">{hot}</div>
    </div>

    <p className="mt-6 text-sm text-[#C4C4C7] italic border-l-2 border-[#D4AF37]/40 pl-4">{note}</p>
  </div>
);

const RiskCard = ({ level, title, icon: Icon, body, signs }) => {
  const color = level === "CRITIQUE" ? "text-[#FF3B30] border-[#FF3B30]/30" : level === "ÉLEVÉ" ? "text-[#D4AF37] border-[#D4AF37]/30" : "text-[#A1A1AA] border-white/15";
  return (
    <div className="border border-white/10 bg-[#0c0c0c] p-6">
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-[#D4AF37]" />
        <span className={`text-[10px] uppercase tracking-[0.3em] px-2 py-1 border ${color}`}>Risque {level}</span>
      </div>
      <div className="font-serif text-xl mt-4 text-white">{title}</div>
      <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">{body}</p>
      <div className="mt-5 pt-5 border-t border-white/10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Signaux à vérifier</div>
        <ul className="space-y-2 text-xs text-[#E4E4E7]">
          {signs.map((s, i) => (
            <li key={i} className="flex gap-2"><span className="text-[#D4AF37]">·</span><span>{s}</span></li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const DocRef = ({ doc, onDownload, accent, note }) => {
  if (!doc) return null;
  return (
    <div className="border border-white/10 bg-[#0c0c0c] p-6 group hover:border-[#D4AF37]/30 transition-colors">
      <div className="flex items-center gap-3">
        <FileText className="h-4 w-4 text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{accent}</span>
      </div>
      <div className="font-serif text-xl mt-3">{doc.name}</div>
      <p className="text-sm text-[#A1A1AA] mt-3">{note}</p>
      <button
        onClick={() => onDownload(doc)}
        data-testid={`watches-download-${doc.id}`}
        className="mt-5 inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
      >
        <Download className="h-3.5 w-3.5" /> Télécharger PDF
      </button>
    </div>
  );
};
